import { Component, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import 'rxjs/add/operator/debounceTime';
import { JSONSchema, SchemaButton, SchemaButtonGroup, SchemaButtonList, WidgetObject, FormElementToast } from './types/json-schema.d';
import { SchemaModule, ModuleValue, ModuleHasProms } from './forms/module';
import { loadSchemaModule, isSchemaButtonGroup } from './forms/schemas';
import { FormComponent } from './form.component';
import { ObjectProperty } from './formproperty';
import { PropertyGroup } from './angular2-schema-form-shim';

@Component( {
  selector: 'sp-module-form',
  template: '<sp-form #form [schema]="schema" [validators]="validators" [model]="model" [actions]="actions" [toast]="toast"></sp-form>',
  styleUrls: [ './app.component.css' ],
} )
export class AppComponent {
  schemaModule: SchemaModule;
  model: ModuleValue = {
    id: null,
    parent_id: null,
    location: null,
    date_time: ( new Date() ).toISOString().split( 'T' )[ 0 ],
    incomplete: false,
    draft: false,
    user_id: null,
  };
  @ViewChild( 'form' ) form: FormComponent;

  private _formActions: SchemaButtonList;
  private _toast: FormElementToast = { content: '' };

  constructor( private elementRef: ElementRef, private changeDetector: ChangeDetectorRef ) {
    this.reloadSchema();

    this.formActions = [
      {
        id: 'save',
        label: 'Save Case',
        class: 'btn-primary',
        action: ( property: ObjectProperty ) => {
          property.value.draft = false;

          this.getAllErrors();
          if ( property.valid ) {
            this.emitExternalEvent( 'save', property.exportValue() );
          } else {
            this.emitExternalEvent( 'errors', property._errors );
          }
        },
      },
      {
        id: 'saveDraft',
        label: 'Save Draft',
        class: 'btn-default',
        action: ( property: ObjectProperty ) => {
          property.value.draft = true;
          this.emitExternalEvent( 'save', property.exportValue() );
        },
      },
      {
        id: 'cancel',
        label: 'Cancel',
        class: 'btn-link',
        action: ( property: ObjectProperty ) => {
          this.emitExternalEvent( 'cancel', property.exportValue() );
        },
      },
    ];

    this.emitExternalEvent( 'init', {
      loadLocations: this.externalLoadLocations.bind( this ),
      getActions: this.externalGetActions.bind( this ),
      setActions: this.externalSetActions.bind( this ),
      getErrors: this.externalGetErrors.bind( this ),
      getSchema: () => this.schema,
      setSchema: this.overrideSchema.bind( this ),
    } );
  }

  private reloadSchema() {
    let schemaName = this.elementRef.nativeElement.getAttribute( 'schema' );
    this.schemaModule = loadSchemaModule( schemaName );
  }

  private overrideSchema( schema: JSONSchema ) {
    this.schemaModule.schema = schema;
  }

  get schema() {
    return this.schemaModule.schema;
  }

  get validators() {
    return this.schemaModule.validators;
  }

  ngOnInit() {
    this.schemaModule.ngOnInit();
  }

  ngAfterViewInit() {
    this.schemaModule.hook( this.form );

    this.schemaModule.rebuild.subscribe( () => {
      this.changeDetector.detectChanges();
    } );

    this.emitExternalEvent( 'ready', {
      loadData: this.externalLoadData.bind( this ),
      setMessage: this.externalSetMessage.bind( this ),
      setToast: this.externalSetToast.bind( this ),
      subscribeValueChanges: this.externalValueChanges.bind( this ),
      subscribeErrorsChanges: this.externalErrorsChanges.bind( this ),
      subscribeVisibilityChanges: this.externalVisibilityChanges.bind( this ),
      setPropertyValue: this.externalSetPropertyValue.bind( this ),
      getPropertyValue: this.externalGetPropertyValue.bind( this ),
      getModel: this.externalGetModel.bind( this ),
      getFormValue: this.externalGetFormValue.bind( this ),
    } );

    // Force another round of change detection because we injected external data.
    this.changeDetector.detectChanges();
  }

  externalLoadLocations( locations: any ) {
    this.schemaModule.loadLocations( locations );
  }

  /**
   * @param keep Fields of steps 2 and 3 that were supplied on purpose and must survive
   *             the emptying a new sub-record otherwise gets - auto-fill reading a
   *             second operation off the same document. Empty for every other caller.
   */
  externalLoadData( data: ModuleValue, options: { keep?: string[] } = {} ) {
    this.model = data;

    this.checkParentId( options.keep || [] );
    // this.checkProms();
  }

  private checkParentId( keep: string[] = [] ) {
    if ( this.model.parent_id ) {

      // All the fields are contained within a single fieldset
      const properties = this.schema.fieldsets[ 0 ].fields;

      let step = 1;
      for ( const propertyName of properties ) {
        if ( propertyName === 'step-2-heading' ) { step = 2; continue; }
        if ( propertyName === 'step-3-heading' ) { step = 3; continue; }

        const property: JSONSchema = this.schema.properties[ propertyName ];
        const widgetType: string = typeof property.widget === 'string' ? property.widget : property.widget.id;

        if (
          // Disable the entirety of step 1...
          step === 1 &&
          // ... but allow procedure to be modified
          propertyName !== 'main_procedure'
        ) {
          this.disableProperty( property );

        } else if ( !this.model.id ) {
          // Delete values in steps 2 & 3 for new sub-records...
          if (
            // ... except the ones the caller supplied for this sub-record
            !keep.includes( propertyName ) &&
            (
            // ... except for hidden fields
            widgetType !== 'hidden' ||
            // ... but still reset these hidden fields
            propertyName === 'date_time' ||
            propertyName === 'incomplete' ||
            propertyName === 'draft'
            )
          ) {
            delete this.model[ propertyName ];
          }

        }

      }
    }
  }

  disableProperty( property: JSONSchema ) {
    property.readOnly = true;

    if ( property.type === 'object' ) {
      Object.values( property.properties )
        .forEach( this.disableProperty.bind( this ) );
    }
  }

  checkProms() {
    if ( !( 'proms' in this.schema.properties ) ) return;

    let schema = this.schema as JSONSchema<ModuleHasProms>;
    let model = this.model as ModuleHasProms;

    if ( model.proms && model.proms.id && model.proms.message ) {
      ( schema.properties.proms.properties.message.widget as WidgetObject ).content = model.proms.message;
    }

    // Turn off PROMS for existing records
    if ( this.model.id || this.model.parent_id ) {
      schema.properties.proms.properties.follow_up_7.default = false;
    }
  }

  externalSetMessage( message: string ) {
    this.schema.fieldsets[ 0 ].message = message;
  }

  externalGetActions() {
    return this.formActions;
  }

  externalSetActions( actions: any ) {
    this.formActions = actions;
  }

  externalGetErrors() {
    return this.getAllErrors();
  }

  set formActions( buttons: SchemaButtonList ) {
    this._formActions = buttons;

    function clearAction( button: SchemaButton ): SchemaButton {
      return { ...button, action: null };
    }

    function clearActions( button: SchemaButton | SchemaButtonGroup ): SchemaButton | SchemaButtonGroup {
      if ( isSchemaButtonGroup( button ) ) {
        return {
          ...button,
          buttons: button.buttons.map( clearActions ),
        } as SchemaButtonGroup;
      } else {
        return clearAction( button );
      }
    }

    // Clear the button actions before adding them to the schema.
    this.schema.buttons = buttons.map( clearActions );
  }

  get formActions() {
    return this._formActions;
  }

  externalSetToast( newToast: FormElementToast ) {
    const emptyToast: FormElementToast = {
      content: '',
      classes: '',
    };

    Object.assign( this._toast, emptyToast, newToast );

    this.changeDetector.detectChanges();
  }

  externalValueChanges( path: string, func: ( value: any ) => void ) {
    const property = this.form.rootProperty.searchProperty( path );
    if ( property ) property.valueChanges.debounceTime( 1 ).subscribe( func );
  }

  externalErrorsChanges( path: string, func: ( errors: any ) => void ) {
    const property = this.form.rootProperty.searchProperty( path );
    if ( property ) property.errorsChanges.debounceTime( 1 ).subscribe( func );
  }

  externalVisibilityChanges( path: string, func: ( visible: boolean ) => void ) {
    const property = this.form.rootProperty.searchProperty( path );
    if ( property ) property.visibilityChanges.debounceTime( 1 ).subscribe( func );
  }

  emitExternalEvent( eventName: string, data: any ) {
    ( <HTMLElement>this.elementRef.nativeElement ).dispatchEvent( new CustomEvent( eventName, {
      bubbles: true,
      detail: data,
    } ) );
  }

  listenExternalEvent( eventName: string, callback: EventListenerOrEventListenerObject ) {
    ( <HTMLElement>this.elementRef.nativeElement ).addEventListener( eventName, callback );
  }

  getAllErrors() {
    ( <PropertyGroup>this.form.rootProperty ).forEachChildRecursive( property => {
      this.form.rootProperty.extendErrors( property._errors );
    } );
  }

  setDraft( draft: boolean ) {
    this.model.draft = draft;
  }

  get actions() {
    let actions: { [ action: string ]: ( property: ObjectProperty ) => void } = {};
    for ( let button of this.formActions ) {
      let buttons = isSchemaButtonGroup( button ) ? button.buttons : [ button ];

      buttons.forEach( button => {
        if ( button.action ) actions[ button.id ] = button.action.bind( this );
      } );
    }

    return actions;
  }

  get toast() {
    return this._toast;
  }

  externalSetPropertyValue( attrName: any, attrValue: any) {
    this.model[attrName] = attrValue;
  }

  externalGetPropertyValue( attrName: any) {
    return this.model.hasOwnProperty(attrName) ? this.model[attrName] : undefined;
  }

  externalGetModel() {
    return { ...this.model };
  }

  /**
   * The form's live values, including edits the user has not saved yet.
   *
   * getModel() cannot be used for this: [model] is a one-way binding, so this.model is
   * only ever written by externalLoadData() and holds the data as loaded rather than as
   * edited. The edits live on the root property, which is what the save action reads.
   */
  externalGetFormValue() {
    return ( <ObjectProperty>this.form.rootProperty ).exportValue();
  }
}



// WEBPACK FOOTER //
// /var/www/schemas-forms/src/app/app.component.ts